import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import LaTeXRenderer from '../LaTeXRenderer';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, testsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/stats`),
        axios.get(`${API_BASE_URL}/api/tests`)
      ]);
      
      setStats(statsResponse.data);
      setTests(testsResponse.data);
    } catch (error) {
      setError('Failed to load dashboard data');
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleUpdateStatus = async (testId, newStatus) => {
    try {
      await axios.put(`${API_BASE_URL}/api/tests/${testId}`, {
        status: newStatus
      });
      
      // Update local state
      setTests(tests.map(test => 
        test._id === testId ? { ...test, status: newStatus } : test
      ));
    } catch (error) {
      alert('Failed to update test status');
    }
  };

  const handleDownloadPDF = async (test) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${test._id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${test.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download PDF');
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test? This will also delete all submissions.')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/tests/${testId}`);
      setTests(tests.filter(test => test._id !== testId));
      
      // Refresh stats
      const statsResponse = await axios.get(`${API_BASE_URL}/api/admin/stats`);
      setStats(statsResponse.data);
    } catch (error) {
      alert('Failed to delete test');
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <h1>Admin Dashboard</h1>
        <p>Welcome to the IMO Sprint Program administration panel.</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* Quick Actions */}
      <div className="card">
        <h2>Quick Actions</h2>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Link to="/admin/create-test" className="btn btn-primary">
            Create New Test
          </Link>
          <Link to="/admin/manage-mocks" className="btn" style={{ backgroundColor: '#6f42c1', color: 'white', border: '1px solid #6f42c1' }}>
            Manage Mocks
          </Link>
        </div>
      </div>

      {/* User Management */}
      <div className="card">
        <h3>User Management</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Link to="/admin/create-account" className="btn btn-success">
            👤 Create Account
          </Link>
          <Link to="/admin/users" className="btn btn-primary">
            👥 Manage Users
          </Link>
          <Link to="/admin/permissions" className="btn btn-outline" style={{ backgroundColor: '#ff9800', color: 'white', border: '1px solid #ff9800' }}>
            🔒 Permission Management
          </Link>
          <Link to="/admin/teacher-assignments" className="btn" style={{ backgroundColor: '#9c27b0', color: 'white', border: '1px solid #9c27b0' }}>
            👩‍🏫 Teacher Assignments
          </Link>
        </div>
      </div>

      {/* Tests Management */}
      <div className="card">
        <h2>Tests Management</h2>
        
        {tests.length === 0 ? (
          <div className="text-center" style={{ padding: '40px', color: '#6c757d' }}>
            <h3>No Tests Created</h3>
            <p>Create your first mock test to get started.</p>
            <Link to="/admin/create-test" className="btn btn-primary">
              Create Test
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {tests.map((test) => (
              <div key={test._id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', color: '#333' }}>
                      {test.title}
                    </h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span className="badge badge-secondary">
                        Week {test.week}
                      </span>
                      <span 
                        className="badge" 
                        style={{ 
                          backgroundColor: test.status === 'active' ? '#28a745' : test.status === 'closed' ? '#ffc107' : '#6c757d',
                          color: test.status === 'closed' ? 'black' : 'white'
                        }}
                      >
                        {test.status === 'active' ? 'Active' : test.status === 'closed' ? 'Closed' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {test.description && (
                  <div style={{ color: '#666', marginBottom: '16px' }}>
                    <LaTeXRenderer text={test.description} />
                  </div>
                )}
                
                <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                  <div><strong>Problems:</strong> {test.problems.length}</div>
                  <div><strong>Due Date:</strong> {formatDate(test.dueDate)}</div>
                  <div><strong>Created:</strong> {formatDate(test.createdAt)}</div>
                </div>
                
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Link 
                    to={`/admin/grade/${test._id}`} 
                    className="btn btn-primary"
                    style={{ fontSize: '14px', padding: '8px 12px' }}
                  >
                    Grade Submissions
                  </Link>
                  
                  <Link 
                    to={`/admin/edit-test/${test._id}`} 
                    className="btn btn-success"
                    style={{ fontSize: '14px', padding: '8px 12px' }}
                  >
                    Edit Test
                  </Link>
                  
                  <button
                    onClick={() => handleDownloadPDF(test)}
                    className="btn btn-outline"
                    style={{ fontSize: '14px', padding: '8px 12px' }}
                  >
                    Download PDF
                  </button>
                  
                  <select
                    value={test.status}
                    onChange={(e) => handleUpdateStatus(test._id, e.target.value)}
                    className="form-control"
                    style={{ fontSize: '14px', padding: '6px 8px', width: 'auto' }}
                  >
                    <option value="active">🟢 Active</option>
                    <option value="closed">🟡 Closed</option>
                    <option value="inactive">🔴 Inactive</option>
                  </select>
                  
                  {(user.role === 'super-admin' || user.role === 'admin') && (
                    <button
                      onClick={() => handleDeleteTest(test._id)}
                      className="btn btn-danger"
                      style={{ fontSize: '14px', padding: '8px 12px' }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Statistics */}
      {stats && (
        <div className="card">
          <h2>System Statistics</h2>
          <div className="grid grid-3">
            <div className="card">
              <h3 style={{ margin: '0 0 8px 0', color: '#007bff' }}>
                {stats.overview.totalTests}
              </h3>
              <p style={{ margin: 0, color: '#666' }}>Total Tests</p>
              <div style={{ fontSize: '14px', color: '#28a745', marginTop: '4px' }}>
                {stats.overview.activeTests} Active
              </div>
            </div>
            
            <div className="card">
              <h3 style={{ margin: '0 0 8px 0', color: '#28a745' }}>
                {stats.overview.totalStudents}
              </h3>
              <p style={{ margin: 0, color: '#666' }}>Registered Students</p>
            </div>

            {stats.overview.totalGraders !== undefined && (
              <div className="card">
                <h3 style={{ margin: '0 0 8px 0', color: '#17a2b8' }}>
                  {stats.overview.totalGraders}
                </h3>
                <p style={{ margin: 0, color: '#666' }}>Active Graders</p>
              </div>
            )}
            
            <div className="card">
              <h3 style={{ margin: '0 0 8px 0', color: '#ffc107' }}>
                {stats.overview.totalSubmissions}
              </h3>
              <p style={{ margin: 0, color: '#666' }}>Total Submissions</p>
              <div style={{ fontSize: '14px', color: '#dc3545', marginTop: '4px' }}>
                {stats.overview.pendingGrading} Pending Review
              </div>
            </div>
            
            <div className="card">
              <h3 style={{ margin: '0 0 8px 0', color: '#dc3545' }}>
                {stats.overview.pendingGrading}
              </h3>
              <p style={{ margin: 0, color: '#666' }}>Pending Grading</p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Submissions */}
      {stats && stats.recentSubmissions && stats.recentSubmissions.length > 0 && (
        <div className="card">
          <h2>Recent Submissions</h2>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Test</th>
                  <th>Problem</th>
                  <th>Submitted</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentSubmissions.map((submission) => (
                  <tr key={submission._id}>
                    <td>{submission.student?.name || 'Unknown'}</td>
                    <td>Week {submission.test?.week || 'N/A'}</td>
                    <td>Problem {submission.problemNumber}</td>
                    <td>{formatDate(submission.submittedAt)}</td>
                    <td>
                      <span className={`badge ${
                        submission.status === 'graded' ? 'badge-success' : 'badge-warning'
                      }`}>
                        {submission.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Test Statistics */}
      {stats && stats.submissionsByTest && stats.submissionsByTest.length > 0 && (
        <div className="card">
          <h2>Test Statistics</h2>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Week</th>
                  <th>Total Submissions</th>
                  <th>Graded</th>
                  <th>Average Score</th>
                </tr>
              </thead>
              <tbody>
                {stats.submissionsByTest.map((testStat) => (
                  <tr key={testStat._id}>
                    <td>{testStat.testTitle}</td>
                    <td>{testStat.testWeek}</td>
                    <td>{testStat.totalSubmissions}</td>
                    <td>{testStat.gradedSubmissions}</td>
                    <td>{testStat.averageScore || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
